// Everything logged at one gazetteer place: every trip leg and every Captain's
// / Pilot's entry whose route reaches it, over a map of those routes.
//
// The other direction from the rest of the app. Everywhere else a place is
// invisible machinery -- a name resolves through it and a correction feeds back
// into it without anyone seeing a row. Here the row is the question: what have
// I actually logged at Cap Sante?
//
// Read-only, on LogbookYear's terms: gazetteer-resolved display copies, no
// write path of any kind, and nothing handed over on navigation. That last one
// is deliberate rather than an omission -- see placeRecordUrl.
//
// What this page OWNS is the map. The tables, the totals and the empty state
// live in PlaceRecordsCard, because the edit page shows the same answer below
// its own map (PlaceVisitsSection) and there must be one implementation of
// "what is logged here". This page is the one with routes drawn above, so it is
// the one that passes showOnMap.

import { useCallback, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { Link, useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { Spinner } from '../../components';
import { RootCrumbProvider, useRootCrumbNavigate } from '../item/RootCrumb';
import { urlForPlace, urlForPlaces } from '../../fleet-shared/Urls.mjs';
import { ENTRY_ROUTE_KEY } from '../../fleet-shared/TripRoute.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { usePlaceRecords } from './usePlaceRecords';
import { usePlaceMapLegs } from './usePlaceMapLegs';
import { PlaceRecordFailures, PlaceRecordsCard, placeRecordUrl } from './PlaceRecordsCard';

// Lazy: the only import path that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

const PLACES_CRUMB = { name: 'Places', url: urlForPlaces() };

const PlaceVisits = () => {
  const { placeId } = useParams();
  const navigate = useRootCrumbNavigate();

  const placeIndex = usePlaceIndex();
  const { place, records, failures, StatusMessage } = usePlaceRecords(placeId, placeIndex);
  const { mapLegs, anyOnMap, hiddenCount, notOnMapCount } = usePlaceMapLegs(records);

  const onSelect = useCallback((row) => navigate(placeRecordUrl(row)), [navigate]);

  const onSelectLeg = useCallback(
    (leg) => {
      const stored = (records ?? []).find((r) => r.kind === leg.kind && r.id === leg.recordId);
      if (stored) onSelect(stored);
    },
    [records, onSelect]
  );

  const heading = place?.name || 'Place';
  const mapNote = hiddenCount > 0
    ? `showing the ${mapLegs.length} most recent of ${mapLegs.length + hiddenCount} routes`
    : undefined;

  return (
    <RootCrumbProvider crumb={PLACES_CRUMB}>
      <div className="pb-2">
        <Container className="pt-3">
          <h3 className="mb-0">{heading}</h3>
          <p className="text-body-secondary small mb-0">
            Everything logged here.{' '}
            <Link to={urlForPlace(placeId)}>Edit this place</Link>
            {' · '}
            <Link to={urlForPlaces()}>All places</Link>
          </p>
        </Container>

        <StatusMessage />

        <PlaceRecordFailures failures={failures} />

        {anyOnMap && (
          <Suspense fallback={<Spinner />}>
            <TripGroupMap
              legs={mapLegs}
              onSelectLeg={onSelectLeg}
              routeKey={ENTRY_ROUTE_KEY}
              title="Routes"
              showWeather={false}
              // Every record here is its own chain -- fifty visits to one
              // harbour are fifty unrelated voyages, not one itinerary. Without
              // this they share TripGroupMap's default single chain and only
              // the last of them keeps an end marker.
              chainKeyFor={(leg) => leg.id}
            />
          </Suspense>
        )}

        <PlaceRecordsCard
          place={place}
          records={records}
          placeIndex={placeIndex}
          onSelect={onSelect}
          heading={heading}
          mapNote={mapNote}
          notOnMapCount={notOnMapCount}
        />
      </div>
    </RootCrumbProvider>
  );
};

export default PlaceVisits;
